import React, { createContext, useReducer, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import AddressInformation from "./components/AddressInformation";
import LoadsInformation from "./components/LoadsInformation";
import ProgressBar from "./components/ProgressBar";
import useHttp from "../../../hooks/useHttp";
import { SINGLE_LOAD_POST_GET_URL } from "../../../utils/api-ends";

/**
 *We use the useReducer hook to create a state and dispatch function
 *that we can use to update the state.
 *And we use the useContext hook to access the state and dispatch function
 *from the context.
 */

export const ManageLoadPostContext = createContext();

const initialLoadPostState = {
  stepNumber: 1,
  isForUpdate: false,
  loadPostData: {},
  minimum_price_to_pay_carrier: "",
  loadPostInfo: {
    origin_contact_name: "",
    origin_contact_phone_number: "",
    origin_address: "",
    origin_city: "",
    origin_state: "",
    origin_country: "",
    origin_postal_code: "",
    origin_latitude: "",
    origin_longitude: "",
    is_origin_auction: false,
    destination_contact_name: "",
    destination_contact_phone_number: "",
    destination_address: "",
    destination_city: "",
    destination_state: "",
    destination_country: "",
    destination_postal_code: "",
    destination_latitude: "",
    destination_longitude: "",
    is_destination_port: false,

    // Step 2 info
    additional_information: "",
    pickup_date: "",
    delivery_date: "",
    price_to_pay_carrier: "",
    load_type: "",
    payment_type: "",
    pallet_list: [],
    barrel_list: [],
    stones_list: [],
    sand_list: [],
    laterite_list: [],
    vehicle_list: [],
  },
  loadPostInfoErrors: {
    origin_contact_name: [],
    origin_contact_phone_number: [],
    origin_address: [],
    origin_city: [],
    origin_state: [],
    origin_country: [],
    origin_postal_code: [],
    origin_latitude: [],
    origin_longitude: [],
    is_origin_auction: [],
    destination_contact_name: [],
    destination_contact_phone_number: [],
    destination_address: [],
    destination_city: [],
    destination_state: [],
    destination_country: [],
    destination_postal_code: [],
    destination_latitude: [],
    destination_longitude: [],
    is_destination_port: [],
    // Step 2 errors
    additional_information: [],
    pickup_date: [],
    delivery_date: [],
    price_to_pay_carrier: [],
    load_type: [],
    payment_type: [],
    stones: [],
    sand: [],
    laterite: [],
    vehicle: [],
    pallet: [],
    barrel: [],
  },
  addressInformationMapInfo: {},
};

const reducer = (state, action) => {
  switch (action.type) {
    case "CHANGE_STEP":
      return {
        ...state,
        stepNumber: action.payload,
      };
    case "MINIMUM_PRICE_TO_PAY":
      return {
        ...state,
        minimum_price_to_pay_carrier: action.payload,
      };
    case "ON_CHANGE_LOAD_POST_INFO":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          [action.payload.name]: action.payload.value,
        },
      };
    case "MANAGE_ERROR_MESSAGES":
      return {
        ...state,
        loadPostInfoErrors: {
          ...state.loadPostInfoErrors,
          [action.payload.name]: action.payload.value,
        },
      };
    case "CLEAR_STEP_ONE_ERROR_MESSAGES":
      return {
        ...state,
        loadPostInfoErrors: {
          ...state.loadPostInfoErrors,
          origin_contact_name: [],
          origin_contact_phone_number: [],
          origin_address: [],
          origin_city: [],
          origin_state: [],
          origin_postal_code: [],
          origin_latitude: [],
          origin_longitude: [],
          is_origin_auction: [],
          destination_contact_name: [],
          destination_contact_phone_number: [],
          destination_address: [],
          destination_city: [],
          destination_state: [],
          destination_postal_code: [],
          destination_latitude: [],
          destination_longitude: [],
          is_destination_port: [],
        },
      };

    case "CLEAR_LOAD_POST_DATA":
      return {
        ...state,
        loadPostInfo: {
          ...initialLoadPostState.loadPostInfo
        }
      }
    case "CLEAR_STEP_TWO_ERROR_MESSAGES":
      return {
        ...state,
        loadPostInfoErrors: {
          ...state.loadPostInfoErrors,
          additional_information: [],
          pickup_date: [],
          delivery_date: [],
          price_to_pay_carrier: [],
          load_type: [],
          payment_type: [],
          stones: [],
          sand: [],
          laterite: [],
          vehicle: [],
          pallet: [],
          barrel: [],
        },
      };

    case "ADD_PALLET_LOADER_TO_LIST":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          pallet_list: [...state.loadPostInfo.pallet_list, action.payload],
        },
      };
    case "UPDATE_PALLET_LOADER_TO_LIST":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          pallet_list: state.loadPostInfo.pallet_list.map((pallet) => {
            if (pallet.id === action.payload.id) {
              return {
                ...pallet,
                ...action.payload,
              };
            }
            return pallet;
          }),
        },
      };
    case "REMOVE_PALLET_LOADER_FROM_LIST":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          pallet_list: state.loadPostInfo.pallet_list.filter(
            (pallet) => pallet.id !== action.payload
          ),
        },
      };

    case "ADD_BARREL_LOADER_TO_LIST":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          barrel_list: [...state.loadPostInfo.barrel_list, action.payload],
        },
      };
    case "UPDATE_BARREL_LOADER_TO_LIST":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          barrel_list: state.loadPostInfo.barrel_list.map((barrel) => {
            if (barrel.id === action.payload.id) {
              return {
                ...barrel,
                ...action.payload,
              };
            }
            return barrel;
          }),
        },
      };
    case "REMOVE_BARREL_LOADER_FROM_LIST":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          barrel_list: state.loadPostInfo.barrel_list.filter(
            (barrel) => barrel.id !== action.payload
          ),
        },
      };

    case "ADD_STONES_LOADER_TO_LIST":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          stones_list: [...state.loadPostInfo.stones_list, action.payload],
        },
      };
    case "UPDATE_STONES_LOADER_TO_LIST":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          stones_list: state.loadPostInfo.stones_list.map((stones) => {
            if (stones.id === action.payload.id) {
              return {
                ...stones,
                ...action.payload,
              };
            }
            return stones;
          }),
        },
      };
    case "REMOVE_STONES_LOADER_FROM_LIST":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          stones_list: state.loadPostInfo.stones_list.filter(
            (stones) => stones.id !== action.payload
          ),
        },
      };

    case "ADD_SAND_LOADER_TO_LIST":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          sand_list: [...state.loadPostInfo.sand_list, action.payload],
        },
      };
    case "UPDATE_SAND_LOADER_TO_LIST":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          sand_list: state.loadPostInfo.sand_list.map((sand) => {
            if (sand.id === action.payload.id) {
              return {
                ...sand,
                  ...action.payload,
                };
              }
              return sand;
            }),
          },
        };
      case "REMOVE_SAND_LOADER_FROM_LIST":
        return {
          ...state,
          loadPostInfo: {
            ...state.loadPostInfo,
            sand_list: state.loadPostInfo.sand_list.filter(
              (sand) => sand.id !== action.payload
            ),
          },
        };
    
    case "ADD_LATERITE_LOADER_TO_LIST":
      return {
        ...state,
        loadPostInfo: {
              ...state.loadPostInfo,
              laterite_list: [...state.loadPostInfo.laterite_list, action.payload],
            },
          };
        case "UPDATE_LATERITE_LOADER_TO_LIST":
          return {
            ...state,
            loadPostInfo: {
              ...state.loadPostInfo,
              laterite_list: state.loadPostInfo.laterite_list.map((laterite) => {
                if (laterite.id === action.payload.id) {
                  return {
                    ...laterite,
                      ...action.payload,
                    };
                  }
                  return laterite;
                }),
              },
            };
          case "REMOVE_LATERITE_LOADER_FROM_LIST":
            return {
              ...state,
              loadPostInfo: {
                ...state.loadPostInfo,
                laterite_list: state.loadPostInfo.laterite_list.filter(
                  (laterite) => laterite.id !== action.payload
                ),
              },
            };

    case "ADD_VEHICLE_LOADER_TO_LIST":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          vehicle_list: [...state.loadPostInfo.vehicle_list, action.payload],
        },
      };
    case "UPDATE_VEHICLE_LOADER_TO_LIST":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          vehicle_list: state.loadPostInfo.vehicle_list.map((vehicle) => {
            if (vehicle.id === action.payload.id) {
              return {
                ...vehicle,
                ...action.payload,
              };
            }
            return vehicle;
          }),
        },
      };
    case "REMOVE_VEHICLE_LOADER_FROM_LIST":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,
          vehicle_list: state.loadPostInfo.vehicle_list.filter(
            (vehicle) => vehicle.id !== action.payload
          ),
        },
      };

    case "APPEND_ERROR_MESSAGES":
      return {
        ...state,
        loadPostInfoErrors: {
          ...state.loadPostInfoErrors,
          ...action.payload,
        },
      };

    case "SET_LOAD_POST_INFO_FOR_EDIT":
      // Avoid unnecessary fields
      Object.keys(state.loadPostInfo).forEach((key) => {
        if (
          key === "origin_latitude" ||
          key === "origin_longitude" ||
          key === "destination_latitude" ||
          key === "destination_longitude"
        ) {
          if (!["", null, undefined].includes(action.payload[key])) {
            state.loadPostInfo[key] = parseFloat(action.payload[key]);
          }
        } else {
          state.loadPostInfo[key] = action.payload[key];
        }
      });
      return {
        ...state,
        isForUpdate: true,
        loadPostInfo: {
          ...state.loadPostInfo,
          pallet_list: action.payload.pallet?.map((item) => ({
            ...item,
            is_for_update: true,
            isExpand: false, // Initially close collapse
          })),
          barrel_list: action.payload.barrel?.map((item) => ({
            ...item,
            is_for_update: true,
            isExpand: false,
          })),
          stones_list: action.payload.stones?.map((item) => ({
            ...item,
            is_for_update: true,
            isExpand: false,
          })),
          sand_list: action.payload.sand?.map((item) => ({
            ...item,
            is_for_update: true,
            isExpand: false,
          })),
          laterite_list: action.payload.laterite?.map((item) => ({
            ...item,
            is_for_update: true,
            isExpand: false,
          })),
          vehicle_list: action.payload.vehicle?.map((item) => ({
            ...item,
            is_for_update: true,
            isExpand: false,
          })),
          origin_contact_phone_number: action?.payload?.origin_contact_phone_number?.split("+1")[1],
          destination_contact_phone_number: action?.payload?.destination_contact_phone_number?.split("+1")[1]
        },
      };

    case "TOGGLE_VEHICLE_LOAD_ITEM_COLLAPSE":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,

          vehicle_list: state.loadPostInfo.vehicle_list.map((vehicle) => {
            if (vehicle.id === action.payload) {
              return {
                ...vehicle,
                isExpand: !vehicle.isExpand,
              };
            }
            return vehicle;
          }),
        },
      };
    case "TOGGLE_STONES_LOAD_ITEM_COLLAPSE":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,

          stones_list: state.loadPostInfo.stones_list.map((stones) => {
            if (stones.id === action.payload) {
              return {
                ...stones,
                isExpand: !stones.isExpand,
              };
            }
            return stones;
          }),
        },
      };
    case "TOGGLE_SAND_LOAD_ITEM_COLLAPSE":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,

          sand_list: state.loadPostInfo.sand_list.map((sand) => {
            if (sand.id === action.payload) {
              return {
                ...sand,
                isExpand: !sand.isExpand,
              };
            }
            return sand;
          }),
        },
      };

    case "TOGGLE_LATERITE_LOAD_ITEM_COLLAPSE":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,  

          laterite_list: state.loadPostInfo.laterite_list.map((laterite) => {
            if (laterite.id === action.payload) {
              return {
                ...laterite,
                isExpand: !laterite.isExpand,
              };
            }
            return laterite;
          }),
        },
      };
    case "TOGGLE_BARREL_LOAD_ITEM_COLLAPSE":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,

          barrel_list: state.loadPostInfo.barrel_list.map((barrel) => {
            if (barrel.id === action.payload) {
              return {
                ...barrel,
                isExpand: !barrel.isExpand,
              };
            }
            return barrel;
          }),
        },
      };
    case "TOGGLE_PALLET_LOAD_ITEM_COLLAPSE":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,

          pallet_list: state.loadPostInfo.pallet_list.map((pallet) => {
            if (pallet.id === action.payload) {
              return {
                ...pallet,
                isExpand: !pallet.isExpand,
              };
            }
            return pallet;
          }),
        },
      };

    case "OPEN_STONES_LOAD_ITEM_COLLAPSE":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,

          stones_list: state.loadPostInfo.stones_list.map((stones) => {
            if (stones.id === action.payload) {
              return {
                ...stones,
                isExpand: true,
              };
            }
              return stones;
          }),
        },
      };
    case "OPEN_SAND_LOAD_ITEM_COLLAPSE":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,

          sand_list: state.loadPostInfo.sand_list.map((sand) => {
            if (sand.id === action.payload) {
              return {
                ...sand,
                isExpand: true,
              };
            }
            return sand;
          }),
        },
      };

    case "OPEN_LATERITE_LOAD_ITEM_COLLAPSE":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,  

          laterite_list: state.loadPostInfo.laterite_list.map((laterite) => {
            if (laterite.id === action.payload) {
              return {
                ...laterite,
                isExpand: true,
              };
            }
            return laterite;
          }),
        },
      };  
    case "OPEN_VEHICLE_LOAD_ITEM_COLLAPSE":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,

          vehicle_list: state.loadPostInfo.vehicle_list.map((vehicle) => {
            if (vehicle.id === action.payload) {
              return {
                ...vehicle,
                isExpand: true,
              };
            }
            return vehicle;
          }),
        },
      };
    case "OPEN_BARREL_LOAD_ITEM_COLLAPSE":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,

          barrel_list: state.loadPostInfo.barrel_list.map((barrel) => {
            if (barrel.id === action.payload) {
              return {
                ...barrel,
                isExpand: true,
              };
            }
            return barrel;
          }),
        },
      };
    case "OPEN_PALLET_LOAD_ITEM_COLLAPSE":
      return {
        ...state,
        loadPostInfo: {
          ...state.loadPostInfo,

          pallet_list: state.loadPostInfo.pallet_list.map((pallet) => {
            if (pallet.id === action.payload) {
              return {
                ...pallet,
                isExpand: true,
              };
            }
            return pallet;
          }),
        },
      };

    case "SET_LOAD_POST_DATA":
      return {
        ...state,
        loadPostData: action.payload,
      };

    case "SET_ADDRESS_INFORMATION_MAP_INFO":
      return {
        ...state,
        addressInformationMapInfo: action.payload,
      };
    default:
      return state;
  }
};

const ManageLoadPost = ({ isForUpdate }) => {
  const [loadPostState, loadPostContextDispatch] = useReducer(
    reducer,
    initialLoadPostState
  );
  const { loadPostId } = useParams();
  const { sendRequests: fetchSingleLoadPostData } = useHttp();
  const userDetails = useSelector((state) => state.basicInfo.userDetails);

  useEffect(() => {
    if (userDetails?.user_type === "carrier") window.location.href = "/";
  }, [userDetails]);

  // Fetch the Load Feed data from the server and set it to the state for editing
  useEffect(() => {

    // Clear data for first time visit
    loadPostContextDispatch({type: "CLEAR_LOAD_POST_DATA"});

    if (isForUpdate) {
      const response = (data) => {
        loadPostContextDispatch({type: "SET_LOAD_POST_INFO_FOR_EDIT", payload: data});
        loadPostContextDispatch({type: "SET_LOAD_POST_DATA", payload: data});
      };

      fetchSingleLoadPostData(
        {
          url: SINGLE_LOAD_POST_GET_URL(loadPostId),
          method: "get",
        },
        response
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPostId, isForUpdate]);

  useEffect(() => {
    if (
      loadPostState?.loadPostData?.is_owner === false &&
      isForUpdate === true
    ) {
      window.location.href = "/";
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadPostContextDispatch({type: "CHANGE_STEP", payload: 1});
  }, [isForUpdate]);

  return (
    <ManageLoadPostContext.Provider
      value={[loadPostState, loadPostContextDispatch]}>
      <ProgressBar />

      {loadPostState.stepNumber === 1 && (
        <AddressInformation isForUpdate={isForUpdate} />
      )}
      {loadPostState.stepNumber === 2 && (
        <LoadsInformation isForUpdate={isForUpdate} />
      )}
    </ManageLoadPostContext.Provider>
  );
};

export default ManageLoadPost;
